import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { RevealIconButton } from '../../components/ui/RevealIconButton';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyApplication,
  TechnologySecret,
  TechnologySecretUpsertPayload,
  createTechnologySecret,
  deleteTechnologySecret,
  listTechnologyApplications,
  listTechnologySecrets,
  revealTechnologySecret,
  updateTechnologySecret,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
  TECHNOLOGY_SECRET_KIND_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

type BindingFormRow = {
  key: string;
  applicationId: string;
  environmentKind: string;
  purpose: string;
};

type SecretFormState = {
  name: string;
  kind: string;
  value: string;
  expiresAtUtc: string;
  notes: string;
  bindings: BindingFormRow[];
};

function createBindingRow(): BindingFormRow {
  return {
    key: crypto.randomUUID(),
    applicationId: '',
    environmentKind: '',
    purpose: '',
  };
}

const emptyForm: SecretFormState = {
  name: '',
  kind: 'ApiKey',
  value: '',
  expiresAtUtc: '',
  notes: '',
  bindings: [],
};

function toDateInputValue(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function fromDateInputValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return `${trimmed}T00:00:00.000Z`;
}

export function TechnologySecretsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const { alert } = useMessageBox();
  const canWrite = canWriteModule('technology.secrets');
  const [items, setItems] = useState<TechnologySecret[]>([]);
  const [applications, setApplications] = useState<TechnologyApplication[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SecretFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [revealingId, setRevealingId] = useState<string | null>(null);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadOptions(): Promise<void> {
    try {
      const apps = await listTechnologyApplications({ page: 1, pageSize: 200 });
      setApplications(apps.items);
    } catch {
      /* opções auxiliares */
    }
  }

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologySecrets({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        kind: kindFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar cofre de credenciais.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadOptions();
  }, []);

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, kindFilter]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: TechnologySecret): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      kind: item.kind,
      value: '',
      expiresAtUtc: toDateInputValue(item.expiresAtUtc),
      notes: item.notes ?? '',
      bindings: item.bindings.map((binding) => ({
        key: binding.id,
        applicationId: binding.applicationId ?? '',
        environmentKind: binding.environmentKind ?? '',
        purpose: binding.purpose ?? '',
      })),
    });
  }

  function toPayload(): TechnologySecretUpsertPayload {
    return {
      name: form.name.trim(),
      kind: form.kind,
      value: form.value.trim() || null,
      expiresAtUtc: fromDateInputValue(form.expiresAtUtc),
      notes: form.notes.trim() || null,
      bindings: form.bindings
        .filter((binding) => binding.applicationId || binding.environmentKind)
        .map((binding) => ({
          applicationId: binding.applicationId || null,
          environmentKind: binding.environmentKind || null,
          purpose: binding.purpose.trim() || null,
        })),
    };
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    if (!editingId && !form.value.trim()) {
      toast.error('Informe o valor do segredo na criação.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologySecret(editingId, toPayload());
        toast.success('Segredo atualizado.');
        collapseForm(resetForm);
      } else {
        await createTechnologySecret(toPayload());
        toast.success('Segredo criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar segredo.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologySecret): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir segredo',
      message: `Deseja excluir "${item.name}" do cofre?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologySecret(item.id);
      toast.success('Segredo excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir segredo.');
    }
  }

  async function handleReveal(item: TechnologySecret): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Revelar segredo',
      message: `Revelar o valor de "${item.name}"? O acesso é auditável e restrito.`,
      confirmLabel: 'Revelar',
      tone: 'danger',
    });
    if (!accepted) return;

    setRevealingId(item.id);
    try {
      const revealed = await revealTechnologySecret(item.id);
      await alert({
        title: revealed.name,
        message: `Valor (v${revealed.version}):`,
        details: [revealed.value],
        confirmLabel: 'Fechar',
      });
    } catch {
      toast.error('Falha ao revelar segredo.');
    } finally {
      setRevealingId(null);
    }
  }

  function updateBinding(key: string, patch: Partial<BindingFormRow>): void {
    setForm((current) => ({
      ...current,
      bindings: current.bindings.map((binding) =>
        binding.key === key ? { ...binding, ...patch } : binding,
      ),
    }));
  }

  return (
    <PageShell>
      <PageHeader
        title="Cofre de Credenciais"
        description="Senhas, tokens e API keys com vínculo a aplicações e ambientes."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar segredo' : 'Novo segredo'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${form.name || 'segredo selecionado'} - clique para expandir`
              : undefined
          }
          createSummary="Clique para expandir e cadastrar um segredo"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo
                <select
                  className="erp-input"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value })}
                >
                  {TECHNOLOGY_SECRET_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Valor {editingId ? '(deixe em branco para manter)' : ''}
                <input
                  className="erp-input"
                  type="password"
                  autoComplete="new-password"
                  value={form.value}
                  onChange={(event) => setForm({ ...form, value: event.target.value })}
                  required={!editingId}
                />
              </label>
              <label className="erp-label">
                Expira em
                <input
                  className="erp-input"
                  type="date"
                  value={form.expiresAtUtc}
                  onChange={(event) => setForm({ ...form, expiresAtUtc: event.target.value })}
                />
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>

            <div className="mt-4 rounded-lg border border-secondary-200 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-secondary-800">Vínculos</p>
                <button
                  type="button"
                  className="erp-btn-secondary"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      bindings: [...current.bindings, createBindingRow()],
                    }))
                  }
                >
                  Adicionar vínculo
                </button>
              </div>
              {form.bindings.length === 0 ? (
                <p className="text-sm text-secondary-500">Nenhum vínculo informado.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {form.bindings.map((binding) => (
                      <div
                        key={binding.key}
                        className="grid gap-2 rounded-md border border-secondary-100 bg-secondary-50/40 p-3 md:grid-cols-4"
                      >
                        <label className="erp-label">
                          Aplicação
                          <select
                            className="erp-input"
                            value={binding.applicationId}
                            onChange={(event) =>
                              updateBinding(binding.key, {
                                applicationId: event.target.value,
                              })
                            }
                          >
                            <option value="">Opcional</option>
                            {applications.map((application) => (
                              <option key={application.id} value={application.id}>
                                {application.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="erp-label">
                          Ambiente
                          <select
                            className="erp-input"
                            value={binding.environmentKind}
                            onChange={(event) =>
                              updateBinding(binding.key, {
                                environmentKind: event.target.value,
                              })
                            }
                          >
                            <option value="">Opcional</option>
                            {TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="erp-label md:col-span-2">
                          Finalidade
                          <div className="flex gap-2">
                            <input
                              className="erp-input flex-1"
                              value={binding.purpose}
                              onChange={(event) =>
                                updateBinding(binding.key, { purpose: event.target.value })
                              }
                            />
                            <button
                              type="button"
                              className="erp-btn-secondary"
                              onClick={() =>
                                setForm((current) => ({
                                  ...current,
                                  bindings: current.bindings.filter(
                                    (row) => row.key !== binding.key,
                                  ),
                                }))
                              }
                            >
                              Remover
                            </button>
                          </div>
                        </label>
                      </div>
                  ))}
                </div>
              )}
            </div>

            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[200px]"
          value={kindFilter}
          onChange={(event) => {
            setPage(1);
            setKindFilter(event.target.value);
          }}
        >
          <option value="">Todos os tipos</option>
          {TECHNOLOGY_SECRET_KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Versão</th>
                  <th>Expira</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 5 : 4} className="text-center text-secondary-500">
                      Nenhum segredo cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{technologyOptionLabel(TECHNOLOGY_SECRET_KIND_OPTIONS, item.kind)}</td>
                      <td>v{item.version}</td>
                      <td>
                        {item.expiresAtUtc
                          ? new Date(item.expiresAtUtc).toLocaleDateString('pt-BR')
                          : '—'}
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex items-center gap-1">
                            <RevealIconButton
                              disabled={revealingId === item.id}
                              onClick={() => void handleReveal(item)}
                              label="Revelar"
                            />
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
